import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux/es/hooks/useSelector";
import { MessageDisplay } from "../common/messageDisplay";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  TableSortLabel,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import EditIcon from "@mui/icons-material/Edit";
import useHttpRequests from "../../hooks/useHttpRequests";
import "../uiControls/commonStyles.css";
import * as utils from "../common/utils";

const thBackgoundColor = "#3a8ddd";
const thFontColor = "white";
const thFontWeight = "bold";

export const TableWithStickyHeader = (props) => {
  const {
    title,
    pagging,
    columns,
    url,
    sourceObj,
    editHandler,
    updateOpen,
    searchOnColumns,
    changeStatus,
    searchPlace,
  } = props;
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [dataTotal, setDataTotal] = useState(0);
  const [dataActive, setDataActive] = useState(0);
  const [dataInActive, setDataInActive] = useState(0);
  const [isActive, setIsActive] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderBy, setOrderBy] = useState(
    sourceObj === "statuses" ? "batch" : "name"
  );
  const [order, setOrder] = useState("asc");
  const facilityId = useSelector((state) => state.SelectedValues.facilityId);
  const [searchTimer, setSearchTimer] = useState(null);

  const paperStyles = !pagging
    ? { width: "100%", height: "calc(100vh - 220px)" }
    : { width: "100%", overflow: "hidden" };
  const tableHeight = !pagging
    ? { height: "calc(100vh - 250px)" }
    : { overflow: "hidden" };

  const { isLoading, sendRequest: fetchData } = useHttpRequests();

  useEffect(() => {
    getData();
  }, [
    page,
    !updateOpen,
    isActive,
    order,
    orderBy,
    changeStatus,
    facilityId,
    rowsPerPage,
  ]);

  const getData = () => {
    const handleFetchResponse = (resObj) => {
      if (!resObj || !resObj.isSuccess || resObj.errorResponse) {
        console.log("User fetch response returns error");
        return;
      }
      if (pagging) {
        const countField =
          isActive === null
            ? "recordsCount"
            : isActive
            ? "recordsActive"
            : "recordsInActive";

        setDataCount(
          resObj[countField] ? resObj[countField] : resObj[sourceObj]?.length
        );
        setDataTotal(
          resObj["recordsCount"]
            ? resObj["recordsCount"]
            : resObj[sourceObj].length
        );
        setDataActive(resObj["recordsActive"] ? resObj["recordsActive"] : 0);
        setDataInActive(
          resObj["recordsInActive"] ? resObj["recordsInActive"] : 0
        );
      }
      setData(resObj[sourceObj]);
    };

    const generateQuery = () => {
      let params =
        "pageSize=" +
        rowsPerPage +
        "&pageIndex=" +
        (page + 1) +
        (isActive !== null ? "&isActive=" + isActive : "") +
        "&orderByColumn=" +
        orderBy +
        "&orderByDirection=" +
        order;

      if (searchQuery !== "") {
        params += "&searchText=" + searchQuery;
      }
      return url(params);
    };

    const query = generateQuery();
    if (query !== null) {
      fetchData(
        {
          url: query,
          method: "GET",
          header: true,
        },
        handleFetchResponse
      );
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchQueryChange = (event) => {
    const { value } = event.target;
    setSearchQuery(value);
  };

  const handleSearchQueryKeyPress = (event) => {
    if (event.key === "Enter") {
      setPage(0);
      getData();
    }
  };

  useEffect(() => {
    clearTimeout(searchTimer);
    const timerId = setTimeout(() => {
      setPage(0);
      getData();
    }, 1000);
    setSearchTimer(timerId);
  }, [searchQuery]);

  const handleSort = (fieldName) => {
    const isAsc = orderBy === fieldName && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(fieldName);
  };

  const filteredData =
    searchOnColumns?.length > 0
      ? data?.filter((item) =>
          searchOnColumns.some((column) => {
            const value = item[column];
            if (value !== null) {
              const searchValue = searchQuery?.toLowerCase();

              const dateValue = new Date(value);
              if (!isNaN(dateValue)) {
                const formattedDate = utils.formatDateTimeUS(value);
                return formattedDate.toLowerCase().includes(searchValue);
              }

              if (typeof value === "string") {
                return value.toLowerCase().includes(searchValue);
              }

              return (
                value != null &&
                value.toString().toLowerCase().includes(searchValue)
              );
            }
          })
        )
      : data;

  const sortedData = pagging
    ? data
    : filteredData?.sort((a, b) =>
        orderBy?.toLowerCase() === "active"
          ? order === "asc"
            ? a[orderBy] !== b[orderBy]
              ? 1
              : -1
            : a[orderBy] === b[orderBy]
            ? 1
            : -1
          : typeof a[orderBy] === "string" && typeof b[orderBy] === "string"
          ? order === "asc"
            ? a[orderBy]?.toLowerCase() > b[orderBy]?.toLowerCase()
              ? 1
              : -1
            : a[orderBy]?.toLowerCase() < b[orderBy]?.toLowerCase()
            ? 1
            : -1
          : order === "asc"
          ? a[orderBy] > b[orderBy]
            ? 1
            : -1
          : a[orderBy] < b[orderBy]
          ? 1
          : -1
      );

  const allClickHandler = () => {
    setIsActive(null);
    setPage(0);
  };

  const activeClickHandler = () => {
    setIsActive(true);
    setPage(0);
  };

  const inActiveClickHandler = () => {
    setIsActive(false);
    setPage(0);
  };

  return (
    <div>
      <Backdrop className="backDrop" open={isLoading}>
        <CircularProgress color="inherit" />
        <p>Please wait...</p>
      </Backdrop>
      <MessageDisplay />
      <div style={{ display: "list-item", marginTop: "5px" }}>
        <label className="title">{title}</label>
        {pagging && (
          <>
            <span className="linkButton" onClick={allClickHandler}>
              All ({dataTotal})
            </span>
            <span className="linkButton" onClick={activeClickHandler}>
              Active ({dataActive})
            </span>
            <span className="linkButton" onClick={inActiveClickHandler}>
              Inactive ({dataInActive})
            </span>
          </>
        )}
        <div style={{ marginBottom: 5, float: searchPlace }}>
          <TextField
            size="small"
            placeholder="Search"
            value={searchQuery}
            onKeyUp={handleSearchQueryKeyPress}
            onChange={handleSearchQueryChange}
          />
        </div>
      </div>

      <Paper sx={paperStyles}>
        <TableContainer sx={tableHeight}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns?.map((column, index) => (
                  <TableCell
                    key={column.label + "_" + index}
                    align={column["align"]}
                    style={{
                      backgroundColor: thBackgoundColor,
                      color: thFontColor,
                      fontWeight: thFontWeight,
                      padding: "8px 0px 8px 8px",
                      minWidth: column["minWidth"] ? column["minWidth"] : 150,
                    }}
                  >
                    {column.label !== "" && column["dataType"] !== "number" && (
                      <TableSortLabel
                        active={orderBy === column["dataField"]}
                        direction={order}
                        onClick={() => handleSort(column["dataField"])}
                      >
                        {column.label}
                      </TableSortLabel>
                    )}
                    {column.label !== "" && column["dataType"] === "number" && (
                      <>{column.label}</>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData?.map((row, idx) => {
                return (
                  <TableRow
                    sx={{ padding: "0px" }}
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={"row_" + idx}
                  >
                    {columns.map((col, colIdx) => {
                      const value = row[col["dataField"]];
                      return (
                        <TableCell
                          sx={{ padding: "0px 0px 0px 8px" }}
                          key={"cols_" + colIdx + "_" + row[col["keyField"]]}
                          align={col["align"]}
                        >
                          {row[col["keyField"]] !== value &&
                            col["dataType"] !== "date" && <>{value}</>}
                          {row[col["keyField"]] !== value &&
                            col["dataType"] === "date" && (
                              <>{utils.formatDateTimeUS(value)}</>
                            )}
                          {row[col["keyField"]] === value && !updateOpen && (
                            <EditIcon
                              sx={{ size: "small", cursor: "pointer" }}
                              onClick={(e) =>
                                editHandler(e, row[col["keyField"]])
                              }
                            />
                          )}
                          {typeof value === "boolean" &&
                            (row["active"] ? (
                              <ToggleOnIcon
                                sx={{
                                  color: "green",
                                  fontSize: "2rem",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  changeStatus(
                                    e,
                                    row[col["keyField"]],
                                    false,
                                    row["name"]
                                  );
                                }}
                              />
                            ) : (
                              <ToggleOffIcon
                                sx={{
                                  color: "grey",
                                  fontSize: "2rem",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  changeStatus(
                                    e,
                                    row[col["keyField"]],
                                    true,
                                    row["name"]
                                  );
                                }}
                              />
                            ))}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {pagging && searchQuery === "" && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 15, 20, 25]}
            component="div"
            count={dataCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </div>
  );
};
